import { message } from 'antd';
import { showLoadingWindow } from './loadingWindow';

interface ViewPdfOptions {
  url: string;
  title: string;
  onError?: (error: Error) => void;
  loadingText: string;
}

interface DownloadFileOptions {
  url: string;
  fileName: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  loadingMessage?: string;
  successMessage?: string;
}

interface ViewOrDownloadOptions {
  url: string;
  fileName: string;
  t: (key: string) => string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}


export const isPdfFile = (url: string, fileName?: string): boolean => {
  if (fileName?.toLowerCase().endsWith('.pdf')) {
    return true;
  }

  try {
    const urlPath = new URL(url).pathname;
    return urlPath.toLowerCase().endsWith('.pdf');
  } catch {
    return false;
  }
};


export const getFileExtensionFromUrl = (url: string): string => {
  try {
    const urlPath = new URL(url).pathname;
    const lastDotIndex = urlPath.lastIndexOf('.');
    if (lastDotIndex > 0) {
      return urlPath.substring(lastDotIndex);
    }
  } catch {
    // Ignore URL parsing errors
  }
  return '.pdf'; 
};


export const viewPdfInWindow = async ({
  url,
  title,
  onError,
  loadingText,
}: ViewPdfOptions): Promise<void> => {
  const newWindow = showLoadingWindow(title, loadingText);

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const pdfBlob = new Blob([blob], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(pdfBlob);

    if (newWindow) {
      newWindow.location.href = blobUrl;
    } else {
      window.location.href = blobUrl;
    }
  } catch (error) {
    if (newWindow) {
      newWindow.close();
    }
    console.error('Error viewing PDF:', error);
    if (onError) {
      onError(error instanceof Error ? error : new Error(String(error)));
    }
    throw error;
  }
};


export const downloadFile = async ({
  url,
  fileName,
  onSuccess,
  onError,
  loadingMessage,
  successMessage,
}: DownloadFileOptions): Promise<void> => {
  const hideLoading = message.loading(loadingMessage, 0);

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);

    hideLoading();
    message.success(successMessage);
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    hideLoading();
    console.error('Error downloading file:', error);
    if (onError) {
      onError(error instanceof Error ? error : new Error(String(error)));
    }
    throw error;
  }
};


export const viewOrDownloadFile = async ({
  url,
  fileName,
  t,
  onSuccess,
  onError,
}: ViewOrDownloadOptions): Promise<void> => {
  const urlExtension = getFileExtensionFromUrl(url);
  const finalFileName = fileName.includes('.') ? fileName : `${fileName}${urlExtension}`;
  const isPdf = isPdfFile(url, finalFileName);

  try {
    if (isPdf) {
      await viewPdfInWindow({
        url,
        title: finalFileName,
        onError,
        loadingText: t('loadingDocument'),
      });
    } else {
      await downloadFile({
        url,
        fileName: finalFileName,
        loadingMessage: t('downloadingFile'),
        successMessage: t('fileDownloaded'),
        onSuccess,
        onError,
      });
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : '';
    message.error(t('failedToDownloadReportFile') + (errorMessage ? `: ${errorMessage}` : ''));
  }
};

